import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../config/api";

export interface BusinessAction {
  id: string;
  name: string;
  label: string;
  url?: string;
  icon?: string;
  handler_class?: string;
  handler_path?: string;
  handler_method?: string;
  linktype?: string;
}

interface UseBusinessActionsResult {
  actions: BusinessAction[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// Cache cleared on each page load - set to empty object
const actionCache: Record<string, BusinessAction[]> = {};

// Clear cache when module loads to ensure fresh data
if (typeof window !== "undefined") {
  // Clear on page load
  Object.keys(actionCache).forEach((key) => delete actionCache[key]);
}

export const useBusinessActions = (
  module: string
): UseBusinessActionsResult => {
  const [actions, setActions] = useState<BusinessAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActions = async () => {
    if (!module) {
      setActions([]);
      return;
    }

    // Check local cache first
    if (actionCache[module]) {
      setActions(actionCache[module]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.schemaBusinessActions(module));
      if (!response.ok) {
        throw new Error(
          `Failed to fetch business actions: ${response.statusText}`
        );
      }
      const data = await response.json();
      const fetchedActions = data.actions || [];

      // Cache locally
      actionCache[module] = fetchedActions;
      setActions(fetchedActions);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch business actions"
      );
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    // Clear local cache for this module
    delete actionCache[module];

    // Also trigger backend refresh
    try {
      await fetch(API_ENDPOINTS.schemaRefreshBusinessActions(), {
        method: "POST",
      });
    } catch (err) {
      console.error("Failed to refresh business actions cache:", err);
    }

    await fetchActions();
  };

  useEffect(() => {
    fetchActions();
  }, [module]);

  return { actions, loading, error, refresh };
};

// Helper to get all actions (for admin/config purposes)
export const useAllBusinessActions = (): UseBusinessActionsResult => {
  const [actions, setActions] = useState<BusinessAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.schemaBusinessActions());
      if (!response.ok) {
        throw new Error(
          `Failed to fetch business actions: ${response.statusText}`
        );
      }
      const data = await response.json();
      setActions(data.actions || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch business actions"
      );
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      await fetch(API_ENDPOINTS.schemaRefreshBusinessActions(), {
        method: "POST",
      });
    } catch (err) {
      console.error("Failed to refresh business actions cache:", err);
    }

    await fetchActions();
  };

  useEffect(() => {
    fetchActions();
  }, []);

  return { actions, loading, error, refresh };
};
